import { decodeErrorResult } from "viem";

import { SignedTokenPermit } from "domain/tokens";
import { abis } from "sdk/abis";

import { ErrorData, ErrorLike, extendError, parseError } from ".";

export const INVALID_PERMIT_SIGNATURE_ERROR = "Invalid permit signature";
export const EXPIRED_PERMIT_DEADLINE_ERROR = "Expired permit deadline";
export const FAST_EXPRESS_PARAMS_TIMEOUT_ERROR = "fastExpressParams timeout";
const NON_EOA_ACCOUNT_DETECTED_ERROR = "Non-EOA account detected";

export function nonEoaAccountError(chainId: number) {
  return extendError(new Error(NON_EOA_ACCOUNT_DETECTED_ERROR), {
    data: {
      chainId,
    },
  });
}

export function getIsNonEoaAccountError(error: ErrorLike) {
  const parsedError = parseError(error);

  return parsedError?.errorMessage?.includes(NON_EOA_ACCOUNT_DETECTED_ERROR);
}

export function getIsInsufficientExecutionFeeError(
  error: ErrorLike
):
  | { isErrorMatched: false }
  | { isErrorMatched: true; errorData: ErrorData; args: { minExecutionFee: bigint; executionFee: bigint } } {
  const parsedError = parseError(error);

  const isErrorMatched = parsedError?.contractError === "InsufficientExecutionFee";

  if (!isErrorMatched || !parsedError) {
    return {
      isErrorMatched: false,
    };
  }

  const args = { minExecutionFee: parsedError.contractErrorArgs[0], executionFee: parsedError.contractErrorArgs[1] };

  return {
    isErrorMatched: true,
    args,
    errorData: parsedError,
  };
}

export function getIsInvalidSignatureError(error: ErrorLike) {
  const parsedError = parseError(error);

  const isErrorMatched = parsedError?.contractError === "InvalidSignature";

  if (!isErrorMatched || !parsedError) {
    return {
      isErrorMatched: false,
    };
  }

  return {
    isErrorMatched: true,
    errorData: parsedError,
  };
}

export function getIsPermitSignatureErrorOnSimulation(error: ErrorLike) {
  return isPermitExternalCallFailedOnSimulation(error, "invalid signature");
}

export function getIsPermitExpiredDeadlineOnSimulation(error: ErrorLike) {
  return isPermitExternalCallFailedOnSimulation(error, "expired deadline");
}

function isPermitExternalCallFailedOnSimulation(error: ErrorLike, errorSubstring: string) {
  const parsedError = parseError(error);

  if (!parsedError || parsedError.errorContext !== "simulation" || parsedError.contractError !== "ExternalCallFailed") {
    return false;
  }

  try {
    const decodedExternalCallFailed = decodeErrorResult({
      abi: abis.CustomErrors,
      data: parsedError.contractErrorArgs[0],
    });

    const errorArg = decodedExternalCallFailed?.args?.[0];

    return typeof errorArg === "string" && errorArg.includes(errorSubstring);
  } catch {
    return false;
  }
}

export function getInvalidPermitSignatureError({
  isValid,
  error,
  permit,
}: {
  isValid: boolean;
  error: ErrorData | undefined;
  permit: SignedTokenPermit;
}) {
  return extendError(new Error(INVALID_PERMIT_SIGNATURE_ERROR), {
    data: {
      isValid,
      originalError: error,
      spender: permit.spender,
      value: permit.value,
      deadline: permit.deadline,
      onchainParams: {
        name: permit.onchainParams.name,
        version: permit.onchainParams.version,
        nonce: permit.onchainParams.nonce,
      },
    },
  });
}

export function getExpiredPermitDeadlineError({ permit }: { permit: SignedTokenPermit }) {
  return extendError(new Error(EXPIRED_PERMIT_DEADLINE_ERROR), {
    data: {
      spender: permit.spender,
      value: permit.value,
      deadline: permit.deadline,
    },
  });
}

const IGNORED_ESTIMATE_GAS_CONTRACT_ERRORS = ["InsufficientMultichainBalance"];
const IGNORED_ESTIMATE_GAS_MESSAGE_PATTERNS = [
  "ERC20: transfer amount exceeds balance",
  "ERC20: transfer amount exceeds allowance",
];

export function isIgnoredEstimateGasError(error: ErrorLike): boolean {
  const parsed = parseError(error);
  if (!parsed) return false;

  if (parsed.contractError && IGNORED_ESTIMATE_GAS_CONTRACT_ERRORS.includes(parsed.contractError)) {
    return true;
  }

  if (
    parsed.errorMessage &&
    IGNORED_ESTIMATE_GAS_MESSAGE_PATTERNS.some((pattern) => parsed.errorMessage!.includes(pattern))
  ) {
    return true;
  }

  return false;
}

export function getIsPossibleExternalSwapError(error: ErrorLike) {
  const parsedError = parseError(error);

  const isExternalCallError = parsedError?.contractError === "ExternalCallFailed";

  const isPayloadRelatedError = parsedError?.errorMessage?.includes("execution reverted");

  return isExternalCallError || isPayloadRelatedError;
}

export function getIsPriceImpactTooLargeError(error: ErrorLike) {
  const parsedError = parseError(error);

  return (
    parsedError?.contractError === "PriceImpactLargerThanOrderSize" ||
    parsedError?.contractError === "SwapPriceImpactExceedsAmountIn"
  );
}
